import {
  type DeepKeys,
  type FormValidateOrFn,
  type FormAsyncValidateOrFn,
  type ReactFormExtendedApi,
  getBy,
  useStore,
} from "@tanstack/react-form";
import { type InputHTMLAttributes, type ReactNode, useEffect } from "react";
import { type CountryType, validatePostalCode } from "@sellar-io/common";
import {
  InputContainer,
  InputField,
  InputNote,
  InputPostalCode,
} from "../../components/Input";

type CountrySource<TFormData> =
  | { country: CountryType; countryField?: never }
  | { countryField: DeepKeys<TFormData>; country?: never };

type PostalCodeFieldProps<
  TFormData,
  TName extends DeepKeys<TFormData>,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnDynamic extends undefined | FormValidateOrFn<TFormData>,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta,
> = CountrySource<TFormData> & {
  form: ReactFormExtendedApi<
    TFormData,
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >;
  name: TName;
  label?: ReactNode;
  className?: string;
  errorClassName?: string;
  containerClassName?: string;
  validationMode?: "submit" | "changeAndBlur";
} & Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "required" | "disabled" | "placeholder"
  >;

export const TanStackPostalCodeField = <
  TFormData,
  TName extends DeepKeys<TFormData>,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnDynamic extends undefined | FormValidateOrFn<TFormData>,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta,
>({
  form,
  name,
  country,
  countryField,
  label,
  className,
  errorClassName,
  containerClassName,
  required = true,
  disabled,
  placeholder = "Postal code",
  validationMode = "submit",
}: PostalCodeFieldProps<
  TFormData,
  TName,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnDynamic,
  TOnDynamicAsync,
  TOnServer,
  TSubmitMeta
>) => {
  const countryValue: unknown = useStore(form.store, (state) =>
    countryField ? getBy(state.values, countryField) : country,
  );
  const resolvedCountry = typeof countryValue === "string" ? countryValue : "";

  useEffect(() => {
    void form.validateField(name, "change");
  }, [form, name, resolvedCountry]);

  const validate = (value: unknown) => {
    const currentCountry = countryField
      ? form.getFieldValue(countryField)
      : country;
    return validatePostalCode(
      String(value ?? ""),
      String(currentCountry ?? ""),
      required,
    );
  };

  return (
    <form.Field
      name={name}
      validators={{
        onSubmit: ({ value }) => validate(value),
        onChange: ({ value, fieldApi }) =>
          validationMode === "changeAndBlur" ||
          fieldApi.form.state.submissionAttempts > 0
            ? validate(value)
            : undefined,
        onBlur: ({ value }) =>
          validationMode === "changeAndBlur" ? validate(value) : undefined,
      }}
    >
      {({ state, handleChange, handleBlur }) => {
        const errorMessage = state.meta.errors
          .map((error: unknown) => {
            if (
              typeof error === "object" &&
              error !== null &&
              "message" in error
            )
              return String(error.message);
            return String(error);
          })
          .filter(Boolean)
          .join(", ");
        const input = (
          <InputPostalCode
            name={String(name)}
            value={String(state.value ?? "")}
            country={resolvedCountry as CountryType}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={errorMessage ? true : undefined}
            onBlur={handleBlur}
            onChange={(event) =>
              handleChange(event.target.value as typeof state.value)
            }
          />
        );
        const note = errorMessage ? (
          <InputNote type="error">{errorMessage}</InputNote>
        ) : undefined;
        if (label !== undefined)
          return (
            <InputField
              label={label}
              className={
                errorMessage
                  ? [className, errorClassName].filter(Boolean).join(" ")
                  : className
              }
              containerClassName={
                errorMessage
                  ? "border-red-300 focus-within:border-red-500"
                  : containerClassName
              }
              note={note}
            >
              {input}
            </InputField>
          );
        return (
          <div
            className={
              errorMessage
                ? [className, errorClassName].filter(Boolean).join(" ")
                : className
            }
          >
            <InputContainer
              className={
                errorMessage
                  ? "border-red-300 focus-within:border-red-500"
                  : containerClassName
              }
            >
              {input}
            </InputContainer>
            {note}
          </div>
        );
      }}
    </form.Field>
  );
};
